/**
 * OpenAI ChatGPT Ads (oaiq) transport.
 *
 * Mirrors the contract of utils/gtm.ts: fire-and-forget, never throws, and
 * never lets analytics break the app. The SDK loader lives in
 * public/index.html; `init` happens here so the pixel id can come from
 * src/env/config.*.json instead of being hardcoded per environment.
 *
 * Reference: developers.openai.com/ads/{measurement-pixel,supported-events}
 *
 * Two things about the OpenAI API that are easy to get wrong:
 *
 *  1. `amount` is an integer in the currency's ISO 4217 MINOR unit — 12999 is
 *     $129.99. gtm.ts works in MAJOR units (dollars), so the conversion
 *     happens at exactly one boundary: trackSubscriptionCreated below.
 *  2. Event names come from a fixed taxonomy of 13, each bound to exactly one
 *     `data.type`. An event with no conversion-event setting in Ads Manager is
 *     received by OpenAI but never counted, so we send ONLY the two that are
 *     configured: `subscription_created` (plan_enrollment) and
 *     `registration_completed` (customer_action).
 *
 *     `trial_started` is deliberately absent — it has no conversion-event
 *     setting, so it would be discarded on arrival. If one is ever added, the
 *     hook goes back in PaymentStep.tsx where the all-trials case currently
 *     reports nothing.
 *
 * The Conversions API key is a SERVER secret. It must never appear in this
 * bundle, in src/env/config.*.json, or in any REACT_APP_* variable — only the
 * Pixel ID is public.
 */

import { config } from 'env';

declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

/** Ring buffer of conversion event ids already sent from this browser. */
const FIRED_KEY = 'tc.oaiq.fired.v1';
const FIRED_MAX = 50;

let initialized = false;
/**
 * True once the SDK script has actually loaded. The queue stub in index.html is
 * a function whether or not the real SDK ever arrives, so its presence proves
 * nothing — this is the only honest signal that a measure call can be delivered.
 */
let sdkReady = false;

/**
 * Snapshotted at module load: the SPA router rewrites the URL on the first
 * navigation, so ?oppref= is gone by the time a purchase happens.
 */
const initialSearch = typeof window !== 'undefined' ? window.location.search : '';

function pixelId(): string {
  return config.oai_pixel_id || '';
}

/**
 * FNV-1a, 32-bit. The fired ledger only ever answers "have I seen this id
 * before", so it does not need the id itself — storing a digest keeps Stripe
 * subscription and checkout ids out of persistent storage entirely.
 *
 * Not a security control (nothing client-side can be, since any key would ship
 * in the same bundle) — just a smaller footprint for the same behaviour. A
 * collision would skip one duplicate report, which across a 50-entry buffer is
 * vanishingly unlikely and harmless.
 */
function digest(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

function readFired(): string[] {
  try {
    const raw = window.localStorage.getItem(FIRED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function hasFired(eventId: string): boolean {
  return Boolean(eventId) && readFired().includes(digest(eventId));
}

function markFired(eventId: string): void {
  if (!eventId) return;
  try {
    const key = digest(eventId);
    const next = readFired().filter((id) => id !== key);
    next.push(key);
    window.localStorage.setItem(FIRED_KEY, JSON.stringify(next.slice(-FIRED_MAX)));
  } catch {
    // Storage disabled (private mode, quota). A possible re-fire beats throwing
    // inside checkout, and OpenAI dedupes on event_id server-side regardless.
  }
}

/**
 * The browser half of the browser/server deduplication contract.
 *
 * OpenAI matches on {Pixel ID, event name, id}, first write wins. The browser
 * passes it as `event_id`; the Conversions API passes the same string as `id`.
 * Keep in step with:
 *   react-marketing-site/src/lib/oaiq.js -> conversionEventId()
 *   billing-manager/pkg/openaiads/       -> EventIDFor*()  (Phase 2)
 *
 * Three namespaces, in preference order:
 *
 *  `tc_ck_<checkout_ref>` — checkout-v2. ONE id per checkout, which is what a
 *    conversion actually is. Necessary because executeCheckoutPlan can split a
 *    single cart across several Stripe subscriptions (trial-eligible items
 *    always get their own sub), so no single subscription id represents the
 *    purchase. checkout_ref is already sent to billing-manager on every create,
 *    so the Phase 2 server event can resolve the same value.
 *
 *  `tc_sub_<stripe_subscription_id>` — a checkout known to have produced
 *    exactly one subscription (the marketing site's guest/authed flows).
 *
 *  `tc_pi_<payment_intent_id>` — legacy call sites reached via the utils/gtm.ts
 *    fan-out, which only ever see a PaymentIntent.
 *
 * The namespaces are distinct so an id from one can never collide with another.
 */
export function conversionEventId(args: {
  checkoutRef?: string | null;
  subscriptionId?: string | null;
  paymentIntentId?: string | null;
}): string {
  if (args.checkoutRef) return `tc_ck_${args.checkoutRef}`;
  if (args.subscriptionId) return `tc_sub_${args.subscriptionId}`;
  if (args.paymentIntentId) return `tc_pi_${args.paymentIntentId}`;
  return '';
}

/**
 * Initialises the pixel and injects the SDK. Idempotent; safe to call from
 * module scope and from React effects.
 *
 * No-ops entirely when the environment has no pixel id configured (dev,
 * staging), which is also why the SDK <script> is created here rather than
 * sitting unconditionally in public/index.html — no pixel, no third-party
 * request.
 */
export function initOaiq(): void {
  try {
    if (initialized) return;
    const id = pixelId();
    if (!id || typeof window === 'undefined' || typeof window.oaiq !== 'function') return;
    window.oaiq('init', {
      pixelId: id,
      // Verbose SDK logging everywhere except production. Note console.log is
      // stubbed out in prod by src/index.tsx, so this would be invisible there.
      ...(config.env_name === 'prod' ? {} : { debug: true })
    });
    // Queued above, drained once this lands. Matches the async CDN tag in
    // OpenAI's own snippet.
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
    // Delivery is only knowable from here. Without it we would mark events as
    // sent the moment they hit the queue, and an ad blocker or a CDN failure
    // would silently drop every conversion with no retry.
    script.onload = () => {
      sdkReady = true;
    };
    script.onerror = () => {
      // Blocked or unreachable. Leaving sdkReady false means nothing gets
      // marked as fired, so a later attempt is still free to re-send — OpenAI
      // dedupes on event_id, so a duplicate costs a request and a lost
      // conversion costs a sale.
      sdkReady = false;
      console.warn('[oaiq] SDK failed to load; conversions will not be reported');
    };
    document.head.appendChild(script);
    initialized = true;
  } catch {
    // never let analytics break the app
  }
}

/**
 * Enqueues a measure call.
 *
 * Always enqueues when the stub exists — a call made before the SDK lands is
 * drained when it does. But the return value reports DELIVERY, not enqueueing:
 * it is true only once the SDK has actually loaded. Callers use it to decide
 * whether to remember the event as sent, so a blocked SDK never consumes a
 * conversion it cannot report.
 *
 * @returns true only when the SDK is loaded and the call can be delivered.
 */
function measure(eventName: string, data: Record<string, unknown>, options?: Record<string, unknown>): boolean {
  try {
    if (!initialized) initOaiq();
    if (!initialized || typeof window === 'undefined' || typeof window.oaiq !== 'function') return false;
    if (options) window.oaiq('measure', eventName, data, options);
    else window.oaiq('measure', eventName, data);
    return sdkReady;
  } catch {
    // never let analytics break the app
    return false;
  }
}

/**
 * OpenAI requires `currency` whenever `amount` is present, so a zero/unknown
 * amount sends neither field rather than a bogus pair. A $0 conversion with a
 * valid event id is still worth reporting (credit-covered checkouts).
 */
function money(amountCents: number | undefined, currency: string): Record<string, unknown> {
  const amount = Math.round(Number(amountCents));
  if (!Number.isFinite(amount) || amount <= 0) return {};
  return { amount, currency: currency.toUpperCase() };
}

export interface OaiqSubscriptionParams {
  /** Per-checkout ref — preferred, one conversion per checkout. */
  checkout_ref?: string | null;
  /** Stripe subscription id, when the checkout produced exactly one. */
  subscription_id?: string | null;
  /** Stripe PaymentIntent id — fallback for legacy call sites. */
  transaction_id?: string | null;
  /** Amount charged TODAY, in MAJOR units (dollars), matching utils/gtm.ts. */
  value: number;
  currency?: string;
}

/**
 * A paid subscription starting. Fires at most once per event id.
 *
 * `subscription_created` rather than `order_created` because that is the
 * conversion event registered in Ads Manager, and it is the truer description
 * of what Traders Connect sells. Its data shape is `plan_enrollment`, which
 * still carries amount + currency.
 *
 * @returns true when an event was actually sent.
 */
export function trackSubscriptionCreated({
  checkout_ref,
  subscription_id,
  transaction_id,
  value,
  currency = 'USD'
}: OaiqSubscriptionParams): boolean {
  try {
    const eventId = conversionEventId({
      checkoutRef: checkout_ref,
      subscriptionId: subscription_id,
      paymentIntentId: transaction_id
    });
    if (!eventId || hasFired(eventId)) return false;

    const data: Record<string, unknown> = {
      type: 'plan_enrollment',
      // Dollars in, minor units out — the single conversion boundary.
      ...money(Math.round((Number(value) || 0) * 100), currency)
    };

    // Mark only after the SDK accepted the call, so a blocked or not-yet-loaded
    // SDK does not permanently consume the event.
    const sent = measure('subscription_created', data, { event_id: eventId });
    if (sent) markFired(eventId);
    return sent;
  } catch (err) {
    console.warn('[oaiq] subscription_created failed', err);
    return false;
  }
}

/**
 * Account creation. Not deduped — it fires once per successful Auth0 signup,
 * and there is no server-side counterpart to collide with.
 */
export function trackRegistrationCompleted(): void {
  measure('registration_completed', { type: 'customer_action' });
}

function readCookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/**
 * The OpenAI ad click id for the current visitor, or '' when they did not
 * arrive from an ad — which is the common case.
 *
 * Prefers the live URL because it is freshest, then falls back to the
 * first-party cookie the pixel maintains. We deliberately do NOT persist our
 * own copy: the SDK already stores and refreshes __oppref, and a second store
 * would just be a staler duplicate that survives longer than it should.
 */
export function readOppref(): string {
  try {
    return new URLSearchParams(initialSearch).get('oppref') || readCookie('__oppref');
  } catch {
    return '';
  }
}

/** The pixel's opaque browser reference, from its __obref cookie. */
export function readObref(): string {
  return readCookie('__obref');
}
